import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchMyTechnicianProfile,
  fetchTechnician,
  fetchTechnicians,
  createTechnician,
  updateTechnician,
  excludeTechnician,
  reinstateTechnician,
  saveOpsNote,
  applyTechnicianImport,
  type MyTechnicianProfile,
  type Technician,
  type TechnicianDirectoryPage,
  type TechnicianFilters,
  type CreateTechnicianInput,
  type UpdateTechnicianInput,
} from '../api/techniciansApi';
import type { ExclusionReason } from '../api/technicianImportContract';
import { samePopulation } from '../utils/samePopulation';
import { hasPendingAddress } from '../utils/unlocatedAddress';
import { ADMIN_AUDIT_QUERY_KEY } from './useAdminAuditEvents';

const KEY = 'technicians';

// A write moves the referential screens too: a specialty's technician count is derived from the
// links this record owns. Invalidating both trees keeps them from disagreeing.
// CA17 — every write lands a new journal event (J2): the count, the "last modified" line and an
// open drawer refresh without a reload.
const useTechnicianMutation = <TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ['referentials'] });
      qc.invalidateQueries({ queryKey: ADMIN_AUDIT_QUERY_KEY });
    },
  });
};

/**
 * The directory page. Previous rows stay for a page turn and nothing else: keeping them
 * unconditionally held `isLoading` at false, so a filter change left the previous technicians in
 * the table with no loader. `enabled` is for the callers that suggest rather than list, which
 * would otherwise fetch the whole pool the moment their field opened, empty.
 */
export const useTechniciansList = (filters: TechnicianFilters, enabled = true) =>
  useQuery<TechnicianDirectoryPage>({
    queryKey: [KEY, 'list', filters],
    queryFn: () => fetchTechnicians(filters),
    placeholderData: (previous: TechnicianDirectoryPage | undefined, previousQuery) =>
      samePopulation('page', previousQuery?.queryKey?.[2] as TechnicianFilters | undefined, filters)
        ? previous
        : undefined,
    staleTime: 30_000,
    enabled,
  });

const GEOCODE_POLL_MS = 5_000;
const GEOCODE_POLL_TRIES = 3;

/**
 * `enabled` is false while creating, where there is no id to read. A saved address is geocoded
 * after the answer: while one is pending, refetch every 5 s, at most 3 times, so its state lands.
 */
export const useTechnician = (id: number | null) => {
  // The data update at which the pending run started: the interval stays a pure function of it.
  const pendingSince = useRef<number | null>(null);
  return useQuery<Technician>({
    queryKey: [KEY, id],
    queryFn: () => fetchTechnician(id as number),
    enabled: id !== null,
    refetchInterval: ({ state }) => {
      if (!hasPendingAddress(state.data?.addresses ?? [])) {
        pendingSince.current = null;
        return false;
      }
      // Failed fetches count as tries too, or an erroring API would poll forever.
      const fetches = state.dataUpdateCount + state.errorUpdateCount;
      pendingSince.current ??= fetches;
      return fetches - pendingSince.current < GEOCODE_POLL_TRIES ? GEOCODE_POLL_MS : false;
    },
  });
};

export const useCreateTechnician = () =>
  useTechnicianMutation((input: CreateTechnicianInput) => createTechnician(input));

export const useUpdateTechnician = () =>
  useTechnicianMutation(({ id, data }: { id: number; data: UpdateTechnicianInput }) => updateTechnician(id, data));

// A side write answering the whole record: it replaces the cached one, and the list moves too.
// CA17 — an exclusion, a reinstatement or an ops note is its own journal event too.
const useRecordSideWrite = <TArgs>(mutationFn: (args: TArgs) => Promise<Technician>) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (record) => {
      qc.setQueryData([KEY, record.id], record);
      qc.invalidateQueries({ queryKey: [KEY, 'list'] });
      qc.invalidateQueries({ queryKey: ADMIN_AUDIT_QUERY_KEY });
    },
  });
};

/** F9 apply; the 200 and the 409 both invalidate, which is harmless when nothing was written. */
export const useApplyTechnicianImport = () => useTechnicianMutation(applyTechnicianImport);

export const useExcludeTechnician = () =>
  useRecordSideWrite(({ id, reason }: { id: number; reason: ExclusionReason }) => excludeTechnician(id, reason));

export const useReinstateTechnician = () => useRecordSideWrite((id: number) => reinstateTechnician(id));

export const useSaveOpsNote = () =>
  useRecordSideWrite(({ id, text }: { id: number; text: string }) => saveOpsNote(id, text));

/**
 * The technician's own identity, as the back office keeps it. No mutation stands beside it (D11),
 * and no `enabled`: the screen that reads it is his own, so there is never an id to wait for.
 */
export const useMyTechnicianProfile = () =>
  useQuery<MyTechnicianProfile>({
    queryKey: [KEY, 'me'],
    queryFn: fetchMyTechnicianProfile,
  });

